/* eslint-disable @typescript-eslint/no-explicit-any */
import Cookies from 'js-cookie';

import {
  publicApi,
  privateApi,
  attachAuthorizationHeader,
  privateRefreshableApi,
} from '@configs/axios-instances';
import { responseOk } from '@utils/apiUtils';
import { IUser } from '@auth_module/context/AuthContext';
import { STORAGE_KEY } from '@auth_module/constants/storageKeys';

const COOKIES_EXP_DAYS = 2;
const COOKIES_EXP_TWO_HOURS = 1 / 12;

export interface CommonResponse {
  data: any;
  message?: string;
  success?: boolean;
}

export interface IRegisterParams {
  first_name: string;
  last_name: string;
  email: string | null;
  password: string;
  password_confirmation: string;
  invite_token?: string;
  name?: string;
}

export interface IRegisterResponse {
  token: string;
}

export interface ILoginParams {
  email: string | null;
  password: string;
  cid?: string;
}

export interface ILoginResponse {
  data: {
    token: string;
    casted?: boolean;
    admin?: string;
  };
  message?: string;
  success?: boolean;
}

export interface IForgotPassParams {
  email: string | null;
}

export interface IResetPassParams {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

export interface IResendForgotPassParams {
  email: string | null;
}

export interface ICreateUserWhatsNew {
  version: string;
}

export type UserApplication = 'audio-insights' | 'promohub' | 'api' | 'megaphone' | 'organization';

export type SwitchUserAppPayload = {
  app: UserApplication;
};

export interface IAsignAvatarParams {
  user_id: string;
  avatar_id: number;
}

export interface IUserAvatarResponse {
  data: {
    id: number;
    name: string;
    url: string;
  } | null;
  message?: string;
  success?: boolean;
}

export interface IAvatarsResponse {
  data: {
    id: number;
    name: string;
    url: string;
  }[];
  message?: string;
  success?: boolean;
}

/**
 * Register user into system.
 *
 * @param {string} IRegisterParams.invite_token.
 * @param {string} IRegisterParams.first_name.
 * @param {string} IRegisterParams.last_name.
 * @param {string} IRegisterParams.email.
 * @param {string} IRegisterParams.password.
 * @param {string} IRegisterParams.password_confirmation.
 * @returns {Promise}.
 */
export const registerUser = async (params: IRegisterParams): Promise<IRegisterResponse> => {
  return await publicApi.post('/register', params);
};

/**
 * Register direct (without invitation) user into system.
 *
 * @param {string} IRegisterParams.first_name.
 * @param {string} IRegisterParams.last_name.
 * @param {string} IRegisterParams.email.
 * @param {string} IRegisterParams.password.
 * @param {string} IRegisterParams.password_confirmation.
 * @param {string} IRegisterParams.name.
 * @returns {Promise}.
 */
export const registerDirectUser = async (params: IRegisterParams): Promise<IRegisterResponse> => {
  return await publicApi.post('/register-direct', params);
};

/**
 * Login user into system.
 *
 * @param {string} ILoginParams.email.
 * @param {string} ILoginParams.password.
 * @returns {Promise<ILoginResponse>}
 */

export const login = async (params: ILoginParams): Promise<ILoginResponse> => {
  const response = await publicApi.post('/login', params);

  if (responseOk(response)) {
    attachAuthorizationHeader(response.data.token);
    Cookies.set(STORAGE_KEY.auth_token, response.data.token, {
      expires: response.data.casted ? COOKIES_EXP_TWO_HOURS : COOKIES_EXP_DAYS,
    });
  }

  return response;
};

/**
 * Log out of system.
 *
 * @returns {Promise<void>}
 */
export const logout = async (): Promise<void> => {
  await privateRefreshableApi.get('/logout');
  Cookies.remove(STORAGE_KEY.auth_token);
  window.location.reload();
};

/**
 * Refresh token.
 * @returns {Promise<{data: string}>}
 * */
export const refreshToken = async (): Promise<string> => {
  const response = await privateApi.post('/refresh-token');
  attachAuthorizationHeader(response.data);
  Cookies.set(STORAGE_KEY.auth_token, response.data, {
    expires: COOKIES_EXP_DAYS,
  });

  return response.data;
};

/**
 * Fetch current user information.
 *
 * @returns {Promise}
 */

export const fetchUserInfo = async (): Promise<IUser> => {
  return await privateApi.get('/user');
};

/**
 * Forgot password. This will trigger email notification with redirect URL
 *
 * @param {string} IForgotPassParams.email.
 * @return {Promise}
 */

export const forgotPassword = async (params: IForgotPassParams): Promise<CommonResponse> => {
  return await privateApi.post('/forgot-password', params);
};

/**
 * Reset password
 *
 * @param {string} IResetPassParams.email.
 * @param {string} IResetPassParams.token.
 * @param {string} IResetPassParams.password.
 * @param {string} IResetPassParams.password_confirmation.
 * @return {Promise}
 */

export const resetPassword = async (params: IResetPassParams): Promise<any> => {
  return await privateApi.post('/reset-password', params);
};

/**
 * Fetch users organization
 * @param {string} id
 * @return {Promise}
 */

export const fetchUserOrganization = async (id: string): Promise<any> => {
  return await privateApi.get(`/organizations/${id}`);
};

/**
 * Get user feature flags
 *
 * @return {Promise}
 */

export const fetchFeatureFlags = async (): Promise<any> => {
  return await privateApi.get('/user/feature-flags');
};

/**
 * Get all feature flags
 *
 * @return {Promise}
 */

export const fetchAllFeatureFlags = async (): Promise<any> => {
  return await privateApi.get('/feature-flags/list');
};

/**
 * Get all What's New
 *
 * @return {Promise}
 */

export const fetchWhatsNew = async (): Promise<any> => {
  return await privateApi.get('/whats-new');
};

/**
 * Get latest What's New
 *
 * @return {Promise}
 */

export const fetchLatestWhatsNew = async (): Promise<any> => {
  return await privateApi.get('/whats-new/latest');
};

/**
 * Fetch users what's new
 * @param {string} str
 * @return {Promise}
 */

export const fetchUserWhatsNew = async (str: string): Promise<any> => {
  return await privateApi.get(`/user/whats-new?version=${str}`);
};

/**
 * Create users whats new
 * @param {ICreateUserWhatsNew} payload
 * @return {Promise}
 */

export const createUserWhatsNew = async (payload: ICreateUserWhatsNew): Promise<any> => {
  return await privateRefreshableApi.post('user/whats-new/create', payload);
};

/**
 * Verify direct register email
 *
 * @param {string} path
 * @return {Promise}
 */
export const verifyEmail = async (path: string): Promise<any> => {
  return await privateRefreshableApi.get(`/email/verify${path}`);
};

/**
 * Resend verification email
 *
 * @return {Promise}
 */
export const resendEmailVerification = async (): Promise<any> => {
  return await privateRefreshableApi.post('/email/verify/send');
};

/**
 * Switch user application
 *
 * @param {SwitchUserAppPayload} params
 * @return {Promise}
 */
export const switchUserApplication = async (params: SwitchUserAppPayload): Promise<any> => {
  return await privateApi.post('/app/switch', params);
};

/**
 * Get user applications
 *
 * @return {Promise}
 */

export const fetchUserApplications = async (): Promise<any> => {
  return await privateApi.get('/app/list');
};

/**
 * Get user Avatar
 *
 * @param {string | undefined} id
 * @return {Promise<IUserAvatarResponse>}
 */

export const fetchUserAvatar = async (id: string | undefined): Promise<IUserAvatarResponse> => {
  return await privateApi.get(`/avatar/user/${id}`);
};

/**
 * Get all Avatars
 *
 * @return {Promise<IAvatarsResponse>}
 */

export const fetchAvatars = async (): Promise<IAvatarsResponse> => {
  return await privateApi.get('/avatar');
};

/**
 * Update user avatar
 *
 * @param {string} IAsignAvatarParams.user_id.
 * @param {string} IAsignAvatarParams.avatar_id.
 * @return {Promise}
 */

export const updateUserAvatar = async (params: IAsignAvatarParams): Promise<any> => {
  const { user_id, avatar_id } = params;
  return await privateApi.post(`/avatar/${avatar_id}/user/${String(user_id)}`);
};
